import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Ban, Search, Loader2, AlertCircle, Check, Trash2, UserX, Clock,
} from 'lucide-react';

interface BansTabProps {
  serverId: string;
}

interface BanRecord {
  id: string;
  server_id: string;
  user_id: string;
  reason: string | null;
  banned_by: string;
  created_at: string;
  username: string | null;
  avatar_url: string | null;
  banned_by_username: string | null;
}

const BansTab: React.FC<BansTabProps> = ({ serverId }) => {
  const [bans, setBans] = useState<BanRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const fetchBans = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('access_token');
      const res = await axios.get(`${import.meta.env.VITE_API_URL}/servers/${serverId}/bans`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBans(Array.isArray(res.data) ? res.data : []);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load bans');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBans(); }, [serverId]);

  const handleUnban = async (userId: string) => {
    try {
      setError(null);
      const token = localStorage.getItem('access_token');
      await axios.delete(
        `${import.meta.env.VITE_API_URL}/servers/${serverId}/bans/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSuccess('User unbanned');
      setTimeout(() => setSuccess(null), 2000);
      await fetchBans();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to unban user');
    }
  };

  const filtered = bans.filter((b) =>
    (b.username || '').toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-text-primary">Bans</h2>
        <p className="text-xs text-text-muted mt-0.5">
          {bans.length} banned user{bans.length !== 1 ? 's' : ''}.
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="settings-input pl-9"
          placeholder="Search banned users..."
        />
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-error/10 border border-error/30 text-error text-xs">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" /> {error}
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-success/10 border border-success/30 text-success text-xs">
          <Check className="w-3.5 h-3.5 flex-shrink-0" /> {success}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-accent-color" />
        </div>
      ) : filtered.length > 0 ? (
        <div className="space-y-2">
          {filtered.map((ban) => (
            <div
              key={ban.id}
              className="bg-bg-secondary/80 backdrop-blur-sm border border-border-color rounded-xl px-4 py-3 flex items-center justify-between group/row hover:bg-highlight/30 transition-all"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-lg bg-bg-tertiary border border-border-color overflow-hidden flex-shrink-0">
                  {ban.avatar_url ? (
                    <img src={ban.avatar_url} alt="" className="w-full h-full object-cover opacity-50" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-sm font-bold text-text-muted">
                      <UserX className="w-4 h-4" />
                    </div>
                  )}
                </div>

                <div className="min-w-0">
                  <p className="text-sm font-medium text-text-primary truncate">
                    {ban.username || 'Unknown User'}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    {ban.reason && (
                      <span className="text-[11px] text-text-muted truncate max-w-[200px]">
                        Reason: {ban.reason}
                      </span>
                    )}
                    <span className="text-[11px] text-text-muted flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(ban.created_at).toLocaleDateString()}
                    </span>
                    {ban.banned_by_username && (
                      <span className="text-[11px] text-text-muted">
                        by {ban.banned_by_username}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <button
                onClick={() => handleUnban(ban.user_id)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-bg-tertiary hover:bg-success/10 text-text-secondary hover:text-success border border-border-color transition-all opacity-0 group-hover/row:opacity-100"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Unban
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-bg-secondary/80 backdrop-blur-sm border border-border-color rounded-xl p-8 text-center">
          <div className="w-12 h-12 mx-auto rounded-xl bg-accent-color/10 flex items-center justify-center mb-3">
            <Ban className="w-6 h-6 text-accent-color" />
          </div>
          <p className="text-sm font-medium text-text-primary mb-1">No Bans</p>
          <p className="text-xs text-text-muted">
            {search ? 'No banned users match your search.' : 'No users have been banned from this server.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default BansTab;
